import React from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, Heart, User, MessageCircle, Flame, CircleDot, Users, Sprout,
  TrendingUp, TrendingDown, Minus, Calendar, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { 
  ASSESSMENT_DIMENSIONS, 
  getScoreInterpretation,
  getDimensionsForRelationshipType 
} from '@/data/relationshipAssessmentQuestions';
import { SavedAssessment } from '@/hooks/useRelationshipAssessment';
import { format, parseISO } from 'date-fns';

interface AssessmentResultsProps {
  assessment: SavedAssessment;
  previousAssessment?: SavedAssessment | null;
  relationshipType: 'romantic' | 'family' | 'friendship' | 'work' | 'other';
  onRetake: () => void;
}

const ICON_MAP: Record<string, React.ElementType> = {
  Shield, Heart, User, MessageCircle, Flame, CircleDot, Users, Sprout
};

// Map DB columns to dimension scores
function mapAssessmentToScores(
  assessment: SavedAssessment,
  relationshipType: 'romantic' | 'family' | 'friendship' | 'work' | 'other'
) {
  const dimensions = getDimensionsForRelationshipType(relationshipType);
  
  // Map existing columns to dimensions (scores are stored as 1-7, we convert to 3-21 range)
  const columnMapping: Record<string, { column: keyof SavedAssessment; multiplier: number }> = {
    security: { column: 'trust_level', multiplier: 3 },
    connection: { column: 'connection_quality', multiplier: 3 },
    autonomy: { column: 'authenticity', multiplier: 3 },
    communication: { column: 'communication_quality', multiplier: 3 },
    passion: { column: 'overall_satisfaction', multiplier: 3 }, // Approximate for romantic
    boundaries: { column: 'emotional_safety', multiplier: 3 }, // For non-romantic
    teamwork: { column: 'support_received', multiplier: 3 },
    growth: { column: 'overall_satisfaction', multiplier: 3 }
  };

  return dimensions.map(dim => {
    const mapping = columnMapping[dim.id];
    const rawScore = mapping ? (assessment[mapping.column] as number || 0) : 0;
    const score = rawScore * mapping.multiplier;
    const maxScore = 21;
    
    return {
      dimension: dim,
      score,
      maxScore,
      percentage: maxScore > 0 ? (score / maxScore) * 100 : 0,
      interpretation: getScoreInterpretation(score)
    };
  });
}

export function AssessmentResults({ 
  assessment, 
  previousAssessment,
  relationshipType,
  onRetake 
}: AssessmentResultsProps) {
  const scores = mapAssessmentToScores(assessment, relationshipType);
  const previousScores = previousAssessment 
    ? mapAssessmentToScores(previousAssessment, relationshipType)
    : null;

  const overallScore = assessment.relationship_health_score || 0;
  const overallInterpretation = getScoreInterpretation(Math.round(overallScore / 100 * 21));

  const getChangeIndicator = (current: number, previous: number) => {
    const diff = current - previous;
    if (diff > 5) return { icon: TrendingUp, color: 'text-accent', label: 'Improved' };
    if (diff < -5) return { icon: TrendingDown, color: 'text-primary', label: 'Shifted' };
    return { icon: Minus, color: 'text-muted-foreground', label: 'Stable' };
  };

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      {/* Overall Score Card */}
      <Card className="border-none shadow-lg overflow-hidden">
        <div 
          className="h-2"
          style={{ backgroundColor: overallInterpretation.color }}
        />
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl">Your Relationship Snapshot</CardTitle>
              <CardDescription className="flex items-center gap-2 mt-1">
                <Calendar className="w-4 h-4" />
                {format(parseISO(assessment.assessment_date), 'MMMM d, yyyy')}
              </CardDescription>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold" style={{ color: overallInterpretation.color }}>
                {Math.round(overallScore)}%
              </div>
              <Badge 
                variant="secondary"
                style={{ 
                  backgroundColor: `${overallInterpretation.color}20`,
                  color: overallInterpretation.color 
                }}
              >
                {overallInterpretation.label}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">
            {overallInterpretation.description}
          </p>
        </CardContent>
      </Card>

      {/* Dimension Scores */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Dimension Breakdown</CardTitle>
          <CardDescription>
            How different aspects of your relationship are doing
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {scores.map((item, index) => {
            const Icon = ICON_MAP[item.dimension.icon] || Heart;
            const prevScore = previousScores?.find(p => p.dimension.id === item.dimension.id);
            const change = prevScore ? getChangeIndicator(item.percentage, prevScore.percentage) : null;

            return (
              <motion.div
                key={item.dimension.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="space-y-2"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div 
                      className="p-1.5 rounded-lg"
                      style={{ backgroundColor: `${item.dimension.color}20` }}
                    >
                      <Icon 
                        className="w-4 h-4" 
                        style={{ color: item.dimension.color }}
                      />
                    </div>
                    <span className="font-medium text-sm">{item.dimension.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {change && (
                      <change.icon className={`w-4 h-4 ${change.color}`} />
                    )}
                    <span 
                      className="text-sm font-semibold"
                      style={{ color: item.interpretation.color }}
                    >
                      {Math.round(item.percentage)}%
                    </span>
                  </div>
                </div>
                <Progress 
                  value={item.percentage} 
                  className="h-2"
                  style={{ 
                    // @ts-ignore - custom property
                    '--progress-background': item.interpretation.color 
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  {item.interpretation.description}
                </p>
              </motion.div>
            );
          })}
        </CardContent>
      </Card>

      {/* Strengths & Growth Areas */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-green-200 dark:border-green-900">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-green-600 dark:text-green-400">
              Areas of Strength
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              {scores
                .filter(s => s.percentage >= 70)
                .map(s => (
                  <li key={s.dimension.id} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-accent" />
                    {s.dimension.name}
                  </li>
                ))}
              {scores.filter(s => s.percentage >= 70).length === 0 && (
                <li className="text-muted-foreground">Still building your strengths</li>
              )}
            </ul>
          </CardContent>
        </Card>

        <Card className="border-primary/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-primary">
              Areas to Nurture
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              {scores
                .filter(s => s.percentage < 60)
                .map(s => (
                  <li key={s.dimension.id} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                    {s.dimension.name}
                  </li>
                ))}
              {scores.filter(s => s.percentage < 60).length === 0 && (
                <li className="text-muted-foreground">No pressing concerns identified</li>
              )}
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Notes */}
      {assessment.notes && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Your Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {assessment.notes}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex justify-center pt-4">
        <Button onClick={onRetake} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Take Assessment Again
        </Button>
      </div>

      <p className="text-center text-xs text-muted-foreground">
        These reflections are offered with curiosity, not certainty. 
        You know your relationship best.
      </p>
    </div>
  );
}
